// src/components/Admin/AdminConfig.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
    Box, Paper, Typography, Tabs, Tab, Stack, Button, TextField, Snackbar,
    Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, CircularProgress
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import axios from "axios";

// 🆕 Importa la pestaña de administradores
import AdministradoresAdmin from "./AdministradoresAdmin";

// --- util descarga blob ---
function descargarBlob(blob, nombre) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = nombre;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}

export default function AdminConfig() {
    const API = process.env.REACT_APP_API_URL;
    const token = localStorage.getItem("token");
    const auth = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

    // ====== GATE POR CONTRASEÑA (.env) ======
    const navigate = useNavigate();
    const ENV_PASS = (process.env.REACT_APP_ADMIN_CONFIG_PASSWORD || "").trim();
    const [authorized, setAuthorized] = useState(false);
    const [gatePass, setGatePass] = useState("");
    const [gateError, setGateError] = useState("");

    const handleGateSubmit = (e) => {
        e.preventDefault();
        if (!ENV_PASS) {
            setAuthorized(true);
            return;
        }
        if (gatePass.trim() === ENV_PASS) {
            setAuthorized(true);
        } else {
            setGateError("Contraseña incorrecta");
            setTimeout(() => navigate("/admin/colegiados"), 600);
        }
    };

    // ====== UI local ======
    const [tab, setTab] = useState(0);
    const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" });
    const notify = (msg, sev = "success") => setSnack({ open: true, msg, sev });

    // ====== AJUSTES ======
    const [ajustes, setAjustes] = useState({
        horario_laboral_desde: "09:00",
        horario_laboral_hasta: "14:00",
        dias_laborables: "1,2,3,4,5", // L-V
        paginacion_tablas: 25,
        max_archivos_por_colegiado: 20,
    });
    const [cargando_ajustes, setCargandoAjustes] = useState(false);

    const cargarAjustes = async () => {
        try {
            setCargandoAjustes(true);
            const { data } = await axios.get(`${API}/api/admin/config`, { headers: auth });
            setAjustes(data);
        } catch {
            notify("No se pudieron cargar los ajustes", "error");
        } finally {
            setCargandoAjustes(false);
        }
    };

    const guardarAjustes = async () => {
        try {
            await axios.put(`${API}/api/admin/config`, ajustes, { headers: auth });
            notify("Ajustes guardados");
        } catch {
            notify("Error guardando ajustes", "error");
        }
    };

    // ====== SQL ======
    const [sql_texto, setSqlTexto] = useState("SELECT codigo, nombre, email FROM colegiados LIMIT 10;");
    const [sql_cargando, setSqlCargando] = useState(false);
    const [sql_result_cols, setSqlResultCols] = useState([]);
    const [sql_result_rows, setSqlResultRows] = useState([]);
    const [sql_modo, setSqlModo] = useState("LECTURA_LIMITADA"); // LECTURA | LECTURA_LIMITADA | DML

    const ejecutarSQL = async () => {
        setSqlCargando(true);
        try {
            const limpio = sql_texto
                .replace(/\/\*[\s\S]*?\*\//g, "")     // /* ... */
                .replace(/^--.*$/gm, "")              // -- ...
                .replace(/;\s*$/, "")                 // ; final
                .trim();
            const { data } = await axios.post(
                `${API}/api/admin/sql/exec`,
                { sql: limpio, modo: sql_modo, limite: 500 },
                { headers: auth }
            );
            // data = { columns: string[], rows: any[] }
            const cols = (data.columns || []).map((c) => ({ field: c, headerName: c, flex: 1, minWidth: 150 }));
            const rows = (data.rows || []).map((r, i) => ({ id: i, ...r }));
            setSqlResultCols(cols);
            setSqlResultRows(rows);
            notify("Consulta ejecutada");
        } catch (e) {
            notify(e?.response?.data?.message || "Error ejecutando SQL", "error");
        } finally {
            setSqlCargando(false);
        }
    };

    // ====== BACKUP / EXPORT ======
    const [dlgBackup, setDlgBackup] = useState(false);
    const [backup_tipo, setBackupTipo] = useState("DUMP_SQL"); // DUMP_SQL | CSV_COLEGIADOS | ZIP_UPLOADS
    const [backup_cargando, setBackupCargando] = useState(false);

    const lanzarBackup = async () => {
        setBackupCargando(true);
        try {
            const url = `${API}/api/admin/backup/export?tipo=${backup_tipo}`;
            const resp = await axios.get(url, { headers: auth, responseType: "blob" });
            const nombre = backup_tipo === "DUMP_SQL" ? "enfernet_dump.sql"
                : backup_tipo === "CSV_COLEGIADOS" ? "colegiados.csv"
                    : "uploads.zip";
            descargarBlob(resp.data, nombre);
            notify("Exportación completada");
            setDlgBackup(false);
        } catch (e) {
            notify("Error en la exportación", "error");
        } finally {
            setBackupCargando(false);
        }
    };

    // ====== SISTEMA / LOGS ======
    const [system_info, setSystemInfo] = useState(null);
    const [cargando_sistema, setCargandoSistema] = useState(false);
    const [logs, setLogs] = useState("");
    const [cargando_logs, setCargandoLogs] = useState(false);

    const cargarSistema = async () => {
        try {
            setCargandoSistema(true);
            const { data } = await axios.get(`${API}/api/admin/system/info`, { headers: auth });
            setSystemInfo(data);
        } catch {
            notify("No se pudo cargar la información de sistema", "error");
        } finally {
            setCargandoSistema(false);
        }
    };

    const cargarLogs = async () => {
        try {
            setCargandoLogs(true);
            const { data } = await axios.get(`${API}/api/admin/logs/tail?lines=300`, { headers: auth });
            setLogs(data?.contenido || "");
        } catch {
            notify("No se pudieron cargar logs", "error");
        } finally {
            setCargandoLogs(false);
        }
    };

    useEffect(() => {
        if (!authorized) return;
        if (tab === 0) cargarAjustes();
        if (tab === 3) { cargarSistema(); cargarLogs(); }
        // eslint-disable-next-line
    }, [tab, authorized]);

    // ====== RENDER: puerta de contraseña ======
    if (!authorized) {
        return (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 400 }}>
                <Paper sx={{ p: 3, width: "100%", maxWidth: 420 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>Acceso restringido</Typography>
                    <Typography variant="body2" sx={{ mb: 2, opacity: 0.8 }}>
                        Introduce la contraseña para acceder a la configuración avanzada.
                    </Typography>
                    <form onSubmit={handleGateSubmit}>
                        <Stack spacing={2}>
                            <TextField
                                label="Contraseña"
                                type="password"
                                size="small"
                                value={gatePass}
                                onChange={(e) => setGatePass(e.target.value)}
                                error={!!gateError}
                                helperText={gateError || " "}
                                autoFocus
                            />
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <Button variant="outlined" onClick={() => navigate("/admin/colegiados")}>
                                    Cancelar
                                </Button>
                                <Button variant="contained" type="submit">
                                    Acceder
                                </Button>
                            </Stack>
                        </Stack>
                    </form>
                </Paper>
            </Box>
        );
    }

    // ====== RENDER: contenido con pestañas ======
    return (
        <Paper sx={{ p: 2 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>Centro de Administración</Typography>

            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
                <Tab label="Ajustes" />
                <Tab label="SQL" />
                <Tab label="Backup / Exportar" />
                <Tab label="Sistema / Logs" />
                {/* 🆕 Nueva pestaña */}
                <Tab label="Administradores" />
            </Tabs>

            {/* ========== AJUSTES ========== */}
            {tab === 0 && (
                <Stack spacing={2}>
                    {cargando_ajustes ? <CircularProgress /> : (
                        <>
                            <Stack direction="row" spacing={2}>
                                <TextField
                                    label="Horario desde" type="time" size="small"
                                    value={ajustes.horario_laboral_desde || ""}
                                    onChange={(e) => setAjustes(a => ({ ...a, horario_laboral_desde: e.target.value }))}
                                />
                                <TextField
                                    label="Horario hasta" type="time" size="small"
                                    value={ajustes.horario_laboral_hasta || ""}
                                    onChange={(e) => setAjustes(a => ({ ...a, horario_laboral_hasta: e.target.value }))}
                                />
                                <TextField
                                    label="Días laborables (0-6, coma)" size="small"
                                    value={ajustes.dias_laborables || ""}
                                    onChange={(e) => setAjustes(a => ({ ...a, dias_laborables: e.target.value }))}
                                    helperText="0=Domingo ... 6=Sábado"
                                />
                            </Stack>

                            <Stack direction="row" spacing={2}>
                                <TextField
                                    label="Paginación por defecto"
                                    type="number" size="small"
                                    value={ajustes.paginacion_tablas || 25}
                                    onChange={(e) => setAjustes(a => ({ ...a, paginacion_tablas: +e.target.value }))}
                                />
                                <TextField
                                    label="Máx. archivos por colegiado"
                                    type="number" size="small"
                                    value={ajustes.max_archivos_por_colegiado || 20}
                                    onChange={(e) => setAjustes(a => ({ ...a, max_archivos_por_colegiado: +e.target.value }))}
                                />
                            </Stack>

                            <Box>
                                <Button variant="contained" onClick={guardarAjustes}>Guardar ajustes</Button>
                            </Box>
                        </>
                    )}
                </Stack>
            )}

            {/* ========== SQL ========== */}
            {tab === 1 && (
                <Stack spacing={2}>
                    <Stack direction="row" spacing={2}>
                        <TextField
                            label="Modo"
                            select size="small" value={sql_modo}
                            onChange={(e) => setSqlModo(e.target.value)}
                            sx={{ width: 260 }}
                            helperText="LECTURA = solo SELECT. DML = INSERT/UPDATE/DELETE (auditable)."
                        >
                            <MenuItem value="LECTURA">LECTURA (solo SELECT)</MenuItem>
                            <MenuItem value="LECTURA_LIMITADA">LECTURA_LIMITADA (SELECT con límite forzado)</MenuItem>
                            <MenuItem value="DML">DML (INSERT/UPDATE/DELETE)</MenuItem>
                        </TextField>
                        <Button variant="contained" onClick={ejecutarSQL} disabled={sql_cargando}>
                            {sql_cargando ? "Ejecutando..." : "Ejecutar"}
                        </Button>
                    </Stack>

                    <TextField
                        label="SQL"
                        value={sql_texto}
                        onChange={(e) => setSqlTexto(e.target.value)}
                        multiline minRows={5}
                        fullWidth
                    />

                    <Box sx={{ height: 400 }}>
                        <DataGrid
                            rows={sql_result_rows}
                            columns={sql_result_cols}
                            pageSizeOptions={[25, 50, 100]}
                            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                        />
                    </Box>
                </Stack>
            )}

            {/* ========== BACKUP / EXPORTAR ========== */}
            {tab === 2 && (
                <Stack spacing={2}>
                    <Typography variant="body1">
                        Genera exportaciones bajo demanda. Todas las acciones quedan auditadas.
                    </Typography>
                    <Stack direction="row" spacing={2}>
                        <Button variant="outlined" onClick={() => { setBackupTipo("DUMP_SQL"); setDlgBackup(true); }}>
                            Exportar BD (mysqldump)
                        </Button>
                        <Button variant="outlined" onClick={() => { setBackupTipo("CSV_COLEGIADOS"); setDlgBackup(true); }}>
                            Exportar colegiados (CSV)
                        </Button>
                        <Button variant="outlined" onClick={() => { setBackupTipo("ZIP_UPLOADS"); setDlgBackup(true); }}>
                            Exportar /uploads (ZIP)
                        </Button>
                    </Stack>

                    <Dialog open={dlgBackup} onClose={() => setDlgBackup(false)}>
                        <DialogTitle>Confirmar exportación</DialogTitle>
                        <DialogContent>
                            <Typography>
                                ¿Seguro que deseas iniciar la exportación <b>{backup_tipo}</b>?
                            </Typography>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setDlgBackup(false)}>Cancelar</Button>
                            <Button variant="contained" onClick={lanzarBackup} disabled={backup_cargando}>
                                {backup_cargando ? "Generando..." : "Exportar"}
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Stack>
            )}

            {/* ========== SISTEMA / LOGS ========== */}
            {tab === 3 && (
                <Stack spacing={2}>
                    <Stack direction="row" spacing={2}>
                        <Button variant="outlined" onClick={cargarSistema}>
                            Refrescar sistema
                        </Button>
                        <Button variant="outlined" onClick={cargarLogs}>
                            Refrescar logs
                        </Button>
                    </Stack>

                    <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle1" sx={{ mb: 1 }}>Información de sistema</Typography>
                        {cargando_sistema ? <CircularProgress /> : (
                            <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                                {system_info ? JSON.stringify(system_info, null, 2) : "—"}
                            </pre>
                        )}
                    </Paper>

                    <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle1" sx={{ mb: 1 }}>Logs (últimas líneas)</Typography>
                        {cargando_logs ? <CircularProgress /> : (
                            <pre style={{ margin: 0, maxHeight: 350, overflow: "auto" }}>{logs || "—"}</pre>
                        )}
                    </Paper>
                </Stack>
            )}

            {/* ========== ADMINISTRADORES ========== */}
            {tab === 4 && (
                <Box>
                    <AdministradoresAdmin />
                </Box>
            )}

            <Snackbar
                open={snack.open}
                autoHideDuration={3000}
                onClose={() => setSnack({ ...snack, open: false })}
                message={snack.msg}
            />
        </Paper>
    );
}
